import React from 'react';
import './Clients.css';

/*
  Each client gets a custom SVG "logo mark" with:
  - Brand-appropriate color palette
  - Styled initials / icon
  - Industry sector tag
  - Real company info from research
*/
const clients = [
  {
    name: 'Metrolab Engineering Pvt. Ltd.',
    tag: 'Engineering & Toolroom Solutions',
    location: 'Chinchwad, Pune',
    color1: '#1a3a6b',
    color2: '#2563eb',
    initials: 'ME',
    icon: '⚙️',
    shape: 'hex',
  },
  {
    name: 'Miracle Cables (India) Pvt. Ltd.',
    tag: 'Specialty Wires & Cables',
    location: 'Mumbai, Maharashtra',
    color1: '#b91c1c',
    color2: '#ef4444',
    initials: 'MC',
    icon: '🔌',
    shape: 'circle',
  },
  {
    name: 'ADM Aero — Aerospace & Semiconductor Technologies',
    tag: 'Aerospace & Semiconductor',
    location: 'Pune, Maharashtra',
    color1: '#0f4c75',
    color2: '#1565c0',
    initials: 'ADM',
    icon: '✈️',
    shape: 'rect',
  },
  {
    name: 'Alfa Flexitubes Pvt. Ltd.',
    tag: 'Flexible Tubes & Hoses',
    location: 'Pune, Maharashtra',
    color1: '#065f46',
    color2: '#059669',
    initials: 'AF',
    icon: '🔧',
    shape: 'circle',
  },
  {
    name: 'Kraft Powercon India Ltd.',
    tag: 'Power Rectifiers & Electronics',
    location: 'Pune · Ranje MIDC, Khedshivapur',
    color1: '#78350f',
    color2: '#d97706',
    initials: 'KP',
    icon: '⚡',
    shape: 'hex',
  },
  {
    name: 'Group Surya Pvt. Ltd.',
    tag: 'Industrial Group',
    location: 'Chakan MIDC · Indapur, Pune',
    color1: '#7c2d12',
    color2: '#ea580c',
    initials: 'GS',
    icon: '☀️',
    shape: 'circle',
  },
  {
    name: 'Luna Technologies Pvt. Ltd.',
    tag: 'Technology & Engineering',
    location: 'Pune, Maharashtra',
    color1: '#4c1d95',
    color2: '#7c3aed',
    initials: 'LT',
    icon: '🌙',
    shape: 'rect',
  },
  {
    name: 'Arkoo Pre-Build Pvt. Ltd.',
    tag: 'Pre-Fabricated Structures',
    location: 'Pune, Maharashtra',
    color1: '#164e63',
    color2: '#0891b2',
    initials: 'ARK',
    icon: '🏗️',
    shape: 'hex',
  },
  {
    name: 'Minda Corporation Ltd.',
    tag: 'Auto Components & Systems',
    location: 'Chakan MIDC, Pune',
    color1: '#1e3a5f',
    color2: '#2563eb',
    initials: 'MC',
    icon: '🚗',
    shape: 'hex',
  },
];

/* SVG logo mark component */
const LogoMark = ({ client }) => {
  const { color1, color2, initials, shape } = client;
  const id = `grad-${initials.replace(/\s/g, '')}`;

  if (shape === 'circle') {
    return (
      <svg width="64" height="64" viewBox="0 0 64 64">
        <defs>
          <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color1} />
            <stop offset="100%" stopColor={color2} />
          </linearGradient>
        </defs>
        <circle cx="32" cy="32" r="31" fill={`url(#${id})`} />
        <text
          x="32" y="32"
          textAnchor="middle"
          dominantBaseline="central"
          fill="white"
          fontFamily="'Barlow Condensed', Arial, sans-serif"
          fontWeight="800"
          fontSize={initials.length > 2 ? '13' : '17'}
          letterSpacing="0.5"
        >
          {initials}
        </text>
      </svg>
    );
  }

  if (shape === 'hex') {
    return (
      <svg width="64" height="64" viewBox="0 0 64 64">
        <defs>
          <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color1} />
            <stop offset="100%" stopColor={color2} />
          </linearGradient>
        </defs>
        <polygon
          points="32,2 58,17 58,47 32,62 6,47 6,17"
          fill={`url(#${id})`}
        />
        <text
          x="32" y="32"
          textAnchor="middle"
          dominantBaseline="central"
          fill="white"
          fontFamily="'Barlow Condensed', Arial, sans-serif"
          fontWeight="800"
          fontSize={initials.length > 2 ? '13' : '17'}
          letterSpacing="0.5"
        >
          {initials}
        </text>
      </svg>
    );
  }

  // rect / rounded rect
  return (
    <svg width="64" height="64" viewBox="0 0 64 64">
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color1} />
          <stop offset="100%" stopColor={color2} />
        </linearGradient>
      </defs>
      <rect x="2" y="2" width="60" height="60" rx="10" fill={`url(#${id})`} />
      <text
        x="32" y="32"
        textAnchor="middle"
        dominantBaseline="central"
        fill="white"
        fontFamily="'Barlow Condensed', Arial, sans-serif"
        fontWeight="800"
        fontSize={initials.length > 2 ? '13' : '17'}
        letterSpacing="0.5"
      >
        {initials}
      </text>
    </svg>
  );
};

const Clients = () => (
  <section className="clients section-light" id="clients">
    <div className="container">
      <span className="section-label">Our Valued Partners</span>
      <h2 className="section-title">Clients Who <span>Trust Us</span></h2>
      <p className="section-desc" style={{ marginBottom: '2.5rem' }}>
        Proudly serving leading manufacturers and industrial companies across Pune's MIDC corridors since 2017.
      </p>
      <div className="clients-grid">
        {clients.map((c) => (
          <div className="client-card" key={c.name}>
            <div className="client-logo-svg">
              <LogoMark client={c} />
            </div>
            <div className="client-info">
              <div className="client-name">{c.name}</div>
              <div className="client-tag">{c.tag}</div>
              <div className="client-loc">📍 {c.location}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default Clients;
